import React, { useRef, useState as useLocalState, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useClaudeAI } from '../hooks/useClaudeAI';
import { useDialog } from './AppDialog';
import DailyLimitModal from './DailyLimitModal';
import { SmartTextarea } from './RichText';
import { CL_TEMPLATES } from '../data/samples';
import { GripVertical, ChevronDown, Sparkles, User, Briefcase, GraduationCap, Zap, Code2, Heart, Trophy, Award, Globe, Layers, BookOpen, ChevronUp, Medal, HeartHandshake, UserCheck, Building2, Users, TrendingUp, BarChart2, Code, Terminal, Cpu, Wrench, Target, Rocket, Star, Shield, Flag, Smile, MapPin, FileText, Lightbulb, Settings, Bookmark, Calendar, Pen, Folder, Link2, Coffee, Music, Camera, FlaskConical, Atom, Brain, Crown, Flame, Handshake, LifeBuoy, Compass, Languages, Eye, EyeOff, Trash2, CheckCircle2, AlertCircle, Lock, ArrowUp, ArrowDown } from 'lucide-react';

// ── Styles ──────────────────────────────────────────────────────────────────
const inp = { width: '100%', padding: '11px 13px', border: '1px solid #E4E7EC', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'white', fontFamily: 'inherit', color: '#111827', transition: 'border-color 0.15s, box-shadow 0.15s' };
const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' };

const LabelWithImprove = ({ children, onImprove, loading }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
    <label style={{ ...lbl, marginBottom: 0 }}>{children}</label>
    <AIBtn onClick={onImprove} loading={loading}>Improve</AIBtn>
  </div>
);

const AIBtn = ({ onClick, loading, children }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'flex', alignItems: 'center', gap: 4,
    background: loading ? '#E5E7EB' : 'linear-gradient(135deg,#FB923C,#F97316)',
    color: loading ? '#9CA3AF' : 'white', border: 'none', borderRadius: 6, padding: '4px 10px',
    fontSize: 11, cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'Inter, sans-serif',
  }}>
    <Sparkles size={11} /> {children}
  </button>
);

const MoveBtn = ({ onClick, disabled, up }) => (
  <button onClick={onClick} disabled={disabled}
    title={disabled ? undefined : up ? 'Move up' : 'Move down'}
    style={{ background: disabled ? 'transparent' : '#F3F4F6', border: 'none', padding: '3px 4px', cursor: disabled ? 'default' : 'pointer', display: 'flex', color: disabled ? '#D1D5DB' : '#6B7280', borderRadius: 4, transition: 'background 0.12s' }}>
    {up ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
  </button>
);

// ── Entry card: sub-card with header bar ("Work Experience #1" + delete + collapse) ──
const EntryCard = ({ label, onRemove, children, defaultOpen = true, onMoveUp, onMoveDown }) => {
  const [open, setOpen] = useLocalState(defaultOpen);
  return (
    <div className="re-entry-card" style={{ border: '1px solid #EBEDF0', borderRadius: 12, marginBottom: 12, background: 'white', overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#FAFBFC', borderBottom: open ? '1px solid #EFF1F4' : 'none', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {(onMoveUp !== undefined || onMoveDown !== undefined) && (
            <div style={{ display: 'flex', border: '1px solid #E4E7EC', borderRadius: 7, overflow: 'hidden', marginRight: 4 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => onMoveUp?.()} disabled={!onMoveUp} title="Move up" style={{ background: onMoveUp ? '#F4F5F7' : 'transparent', border: 'none', borderRight: '1px solid #E4E7EC', padding: '4px 7px', cursor: onMoveUp ? 'pointer' : 'default', display: 'flex', alignItems: 'center', color: onMoveUp ? '#4B5563' : '#D1D5DB', transition: 'background 0.12s' }} onMouseEnter={e => { if (onMoveUp) e.currentTarget.style.background = '#E9EBF0'; }} onMouseLeave={e => { if (onMoveUp) e.currentTarget.style.background = '#F4F5F7'; }}>
                <ArrowUp size={12} strokeWidth={2.2} />
              </button>
              <button onClick={() => onMoveDown?.()} disabled={!onMoveDown} title="Move down" style={{ background: onMoveDown ? '#F4F5F7' : 'transparent', border: 'none', padding: '4px 7px', cursor: onMoveDown ? 'pointer' : 'default', display: 'flex', alignItems: 'center', color: onMoveDown ? '#4B5563' : '#D1D5DB', transition: 'background 0.12s' }} onMouseEnter={e => { if (onMoveDown) e.currentTarget.style.background = '#E9EBF0'; }} onMouseLeave={e => { if (onMoveDown) e.currentTarget.style.background = '#F4F5F7'; }}>
                <ArrowDown size={12} strokeWidth={2.2} />
              </button>
            </div>
          )}
          {onRemove && (
            <button className="re-ic-btn re-ic-danger" onClick={e => { e.stopPropagation(); onRemove(); }} title="Delete entry"><Trash2 size={15} /></button>
          )}
          <button className="re-ic-btn" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} title={open ? 'Collapse' : 'Expand'}>
            <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
        </div>
      </div>
      {open && <div style={{ padding: 14 }}>{children}</div>}
    </div>
  );
};

// ── Bullet list editor with keyboard flow ─────────────────────────────────────
// Enter → insert a new bullet below and focus it; Backspace on an empty bullet →
// remove it and focus the previous one.
const BulletEditor = ({ bullets, onChange, placeholder = 'Achievement with measurable impact...', accent = '#EF1D28', addLabel = '+ Add bullet' }) => {
  const wrapRef = useRef(null);
  const list = bullets || [];

  const focusIdx = (idx) => setTimeout(() => {
    const inputs = wrapRef.current?.querySelectorAll('input[data-bullet]');
    const el = inputs?.[Math.max(0, Math.min(idx, (inputs?.length || 1) - 1))];
    if (el) { el.focus(); el.selectionStart = el.selectionEnd = el.value.length; }
  }, 40);

  const insertAt = (idx) => { onChange([...list.slice(0, idx + 1), '', ...list.slice(idx + 1)]); focusIdx(idx + 1); };
  const removeAt = (idx) => { onChange(list.filter((_, j) => j !== idx)); focusIdx(idx - 1); };

  return (
    <div ref={wrapRef}>
      {list.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
          <span style={{ color: '#6B7280' }}>•</span>
          <input data-bullet style={{ ...inp, flex: 1 }} value={b} placeholder={placeholder}
            onChange={e => onChange(list.map((x, j) => j === i ? e.target.value : x))}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); insertAt(i); }
              else if (e.key === 'Backspace' && !b && list.length > 1) { e.preventDefault(); removeAt(i); }
            }} />
          <button className="re-x" onClick={() => removeAt(i)} tabIndex={-1} title="Remove bullet">×</button>
        </div>
      ))}
      <button onClick={() => insertAt(list.length - 1)} style={{ fontSize: 11, color: accent, background: 'none', border: 'none', cursor: 'pointer', marginTop: 2 }}>{addLabel}</button>
    </div>
  );
};

// ── Icon picker data ──────────────────────────────────────────────────────────
const ICON_OPTIONS = [
  { id: 'briefcase',      Icon: Briefcase },
  { id: 'graduation-cap', Icon: GraduationCap },
  { id: 'zap',            Icon: Zap },
  { id: 'code2',          Icon: Code2 },
  { id: 'rocket',         Icon: Rocket },
  { id: 'bar-chart',      Icon: BarChart2 },
  { id: 'trending-up',    Icon: TrendingUp },
  { id: 'trophy',         Icon: Trophy },
  { id: 'medal',          Icon: Medal },
  { id: 'award',          Icon: Award },
  { id: 'target',         Icon: Target },
  { id: 'bookmark',       Icon: Bookmark },
  { id: 'wrench',         Icon: Wrench },
  { id: 'flask',          Icon: FlaskConical },
  { id: 'file-text',      Icon: FileText },
  { id: 'book-open',      Icon: BookOpen },
  { id: 'layers',         Icon: Layers },
  { id: 'globe',          Icon: Globe },
  { id: 'languages',      Icon: Languages },
  { id: 'heart',          Icon: Heart },
  { id: 'handshake',      Icon: Handshake },
  { id: 'user',           Icon: User },
  { id: 'users',          Icon: Users },
  { id: 'star',           Icon: Star },
  { id: 'lightbulb',      Icon: Lightbulb },
  { id: 'camera',         Icon: Camera },
  { id: 'music',          Icon: Music },
  { id: 'compass',        Icon: Compass },
  { id: 'brain',          Icon: Brain },
  { id: 'cpu',            Icon: Cpu },
  { id: 'terminal',       Icon: Terminal },
  { id: 'code',           Icon: Code },
  { id: 'settings',       Icon: Settings },
  { id: 'flame',          Icon: Flame },
  { id: 'crown',          Icon: Crown },
  { id: 'shield',         Icon: Shield },
  { id: 'flag',           Icon: Flag },
  { id: 'map-pin',        Icon: MapPin },
  { id: 'calendar',       Icon: Calendar },
  { id: 'pen',            Icon: Pen },
  { id: 'folder',         Icon: Folder },
  { id: 'link2',          Icon: Link2 },
  { id: 'coffee',         Icon: Coffee },
  { id: 'atom',           Icon: Atom },
  { id: 'smile',          Icon: Smile },
  { id: 'building2',      Icon: Building2 },
  { id: 'heart-handshake',Icon: HeartHandshake },
  { id: 'life-buoy',      Icon: LifeBuoy },
];

const ICON_LOOKUP = Object.fromEntries(ICON_OPTIONS.map(({ id, Icon }) => [id, Icon]));

const DEFAULT_SECTION_ICONS = {
  experience:     'briefcase',
  education:      'graduation-cap',
  skills:         'zap',
  projects:       'code2',
  strengths:      'star',
  goals:          'target',
  hobbies:        'heart',
  achievements:   'trophy',
  certifications: 'bookmark',
  awards:         'award',
  languages:      'languages',
  volunteering:   'heart-handshake',
  publications:   'book-open',
  references:     'user',
};

// ── Premium sections context ──────────────────────────────────────────────────
const PremiumSectionsContext = React.createContext([]);

// ── Pro plan context: gates Section Analysis behind premium ──────────────────
const EditorProContext = React.createContext({ isPro: false, requestUpgrade: () => {} });

// ── Section Analysis card (premium): peeks above the section card ────────────
// Renders **bold** markers in analysis messages as bold text
const AnalysisText = ({ text }) =>
  String(text).split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 ? <strong key={i} style={{ color: '#101828', fontWeight: 700 }}>{part}</strong> : part);

const SectionAnalysisCard = ({ expanded, onToggle, loading, result, error, onRetry }) => (
  <div style={{
    background: 'white', border: '1px solid #ECEEF1', borderRadius: 16,
    padding: expanded ? '16px 20px 36px' : '12px 20px 32px',
    marginBottom: -20, boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: expanded ? '#101828' : '#98A2B3' }}>Section Analysis</span>
      <button className="re-ic-btn" onClick={onToggle} title={expanded ? 'Hide Section Analysis' : 'Analyze this section with AI'}>
        {expanded ? <EyeOff size={19} /> : <Eye size={19} />}
      </button>
    </div>
    {expanded && (
      <div style={{ marginTop: 14 }}>
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: '#6B7280', padding: '4px 0' }}>
            <span className="re-spin" style={{ width: 15, height: 15, border: '2px solid #E5E7EB', borderTopColor: '#EF1D28', borderRadius: '50%', display: 'inline-block' }} />
            Analyzing this section…
          </div>
        )}
        {!loading && error && (
          <div style={{ fontSize: 13, color: '#B42318', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>Analysis failed: {error}</span>
            <button onClick={onRetry} style={{ background: 'none', border: 'none', color: '#EF1D28', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Retry</button>
          </div>
        )}
        {!loading && !error && result && (
          <>
            {(result.strengths || []).length > 0 && (
              <div style={{ marginBottom: (result.issues || []).length ? 22 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 13 }}>
                  <CheckCircle2 size={19} color="#12B76A" strokeWidth={2} />
                  <span style={{ fontSize: 15.5, fontWeight: 700, color: '#101828' }}>Strengths</span>
                </div>
                {result.strengths.map((s, i) => (
                  <div key={i} style={{ background: '#EAF4EE', color: '#384250', borderRadius: 10, padding: '13px 15px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 10 }}><AnalysisText text={s} /></div>
                ))}
              </div>
            )}
            {(result.issues || []).length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 13 }}>
                  <AlertCircle size={19} color="#F04438" strokeWidth={2} />
                  <span style={{ fontSize: 15.5, fontWeight: 700, color: '#101828' }}>Issues</span>
                </div>
                {result.issues.map((s, i) => (
                  <div key={i} style={{ background: '#FBEDEB', color: '#384250', borderRadius: 10, padding: '13px 15px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 10 }}><AnalysisText text={s} /></div>
                ))}
              </div>
            )}
            {!(result.strengths || []).length && !(result.issues || []).length && (
              <div style={{ fontSize: 13.5, color: '#027A48' }}>Looks good — no issues found in this section.</div>
            )}
          </>
        )}
      </div>
    )}
  </div>
);

// ── Collapsible section wrapper ───────────────────────────────────────────────
const CollapsibleSection = ({ title, icon: Icon, onAdd, addLabel = '+ Add', dragHandle, onMoveUp, onMoveDown, children, defaultOpen = true, sectionTitle, onTitleChange, sectionIcon, onIconChange, noTitleEdit = false, isPremium = false, sectionKey, count, summary, emptyHint, analysisContent }) => {
  const premiumList = useContext(PremiumSectionsContext);
  const { isPro, requestUpgrade } = useContext(EditorProContext);
  const isPremiumResolved = isPremium || (sectionKey ? premiumList.includes(sectionKey) : false);
  const [open, setOpen] = useLocalState(defaultOpen);
  const [editing, setEditing] = useLocalState(false);
  const [showIconPicker, setShowIconPicker] = useLocalState(false);
  const [pickerPos, setPickerPos] = useLocalState(null);
  const [showAnalysis, setShowAnalysis] = useLocalState(false);
  const [analysisLoading, setAnalysisLoading] = useLocalState(false);
  const [analysisResult, setAnalysisResult] = useLocalState(null);
  const [analysisError, setAnalysisError] = useLocalState(null);
  const bodyRef = useRef(null);
  const displayTitle = sectionTitle || title;

  const exitEditing = () => { setEditing(false); setShowIconPicker(false); };

  const runAnalysis = async () => {
    setAnalysisLoading(true); setAnalysisError(null);
    try {
      const r = await api.analyzeSection({ section: sectionKey || title, title: displayTitle, content: analysisContent });
      setAnalysisResult(r);
    } catch (e) { setAnalysisError(e.message); }
    setAnalysisLoading(false);
  };

  const toggleAnalysis = (e) => {
    e.stopPropagation();
    if (!isPro) { requestUpgrade(); return; }
    if (showAnalysis) { setShowAnalysis(false); return; }
    setShowAnalysis(true);
    runAnalysis();
  };

  // Add flow: expand the section, scroll the new entry into view, focus its first field
  const handleAdd = () => {
    onAdd();
    setOpen(true);
    setTimeout(() => {
      const body = bodyRef.current;
      if (!body) return;
      const cards = body.querySelectorAll('.re-entry-card');
      const target = cards.length ? cards[cards.length - 1] : body;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.querySelector('input:not([type="checkbox"]), textarea')?.focus({ preventScroll: true });
    }, 80);
  };

  const POPUP_H = 340;
  const openPicker = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - 326));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= POPUP_H + 10 ? rect.bottom + 6 : rect.top - POPUP_H - 6;
    setPickerPos({ top, left });
    setShowIconPicker(p => !p);
  };

  const LucideIcon = sectionIcon ? ICON_LOOKUP[sectionIcon] : null;
  const SectionIcon = LucideIcon || Icon || null;

  const canEditIcon = !noTitleEdit && !!onIconChange;
  const canEditTitle = !noTitleEdit && !!onTitleChange;

  return (
    <div style={{ marginBottom: 16 }}>
    {/* Section Analysis card — peeks above the section card */}
    {analysisContent !== undefined && (
      <SectionAnalysisCard expanded={showAnalysis} onToggle={toggleAnalysis} loading={analysisLoading} result={analysisResult} error={analysisError} onRetry={runAnalysis} />
    )}
    <div className="re-sec-card" data-section-anchor={sectionKey || title} style={{
      background: 'white',
      border: '1px solid #ECEEF1',
      borderRadius: 16,
      overflow: 'hidden',
      position: 'relative',
      boxShadow: analysisContent !== undefined ? '0 1px 3px rgba(16,24,40,0.05), 0 -5px 14px rgba(16,24,40,0.06)' : '0 1px 3px rgba(16,24,40,0.05)',
      transition: 'box-shadow 0.2s',
    }}>
      {/* Header row */}
      <div onClick={() => { if (!showIconPicker && !editing) { setOpen(p => !p); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '15px 18px',
          cursor: 'pointer', userSelect: 'none',
          background: 'white',
          minHeight: 60,
        }}>

        {/* Drag grip */}
        {dragHandle && (
          <div className="re-sec-drag" {...dragHandle} onClick={e => e.stopPropagation()}
            style={{
              cursor: 'grab', touchAction: 'none', userSelect: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              width: 22, height: 36, borderRadius: 6, marginLeft: -8, marginRight: -6,
              background: 'transparent', color: '#C2C8D0',
              border: 'none', transition: 'background 0.12s, color 0.12s',
            }} title="Drag to reorder">
            <GripVertical size={14} />
          </div>
        )}

        {/* Section icon in a bordered box with "Edit" tab — click to change icon */}
        {canEditIcon ? (
          <div className="re-icon-edit" onClick={openPicker} title="Click to change the section icon"
            style={{
              position: 'relative', width: 46, height: 46, borderRadius: 10, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'white', border: showIconPicker ? '1.5px solid #EF1D28' : '1.5px solid #E4E7EC',
              cursor: 'pointer', transition: 'border-color 0.15s',
            }}>
            <span style={{ position: 'absolute', top: -8, left: 7, background: 'white', padding: '0 5px', fontSize: 9.5, fontWeight: 600, color: showIconPicker ? '#EF1D28' : '#98A2B3', lineHeight: '14px' }}>Edit</span>
            {SectionIcon
              ? <SectionIcon size={20} color="#344054" strokeWidth={1.8} />
              : <span style={{ fontSize: 9, color: '#98A2B3', fontWeight: 600 }}>None</span>}
          </div>
        ) : SectionIcon && (
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'white', border: '1.5px solid #E4E7EC',
          }}>
            <SectionIcon size={19} color="#344054" strokeWidth={1.8} />
          </div>
        )}

        {/* Title block: click to rename inline */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <input
              autoFocus
              value={sectionTitle || ''}
              onChange={e => onTitleChange(e.target.value)}
              placeholder={title}
              onClick={e => e.stopPropagation()}
              onBlur={exitEditing}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') exitEditing(); }}
              style={{ width: '100%', maxWidth: 360, border: '1.5px solid #EF1D28', borderRadius: 9, padding: '7px 12px', fontSize: 16, fontWeight: 700, color: '#101828', fontFamily: 'inherit', background: '#FFF9F9', outline: 'none', cursor: 'text', boxSizing: 'border-box' }}
            />
          ) : (
            <div className="re-title-wrap" title={canEditTitle ? 'Click to rename this section' : undefined}
              onClick={e => { if (canEditTitle) { e.stopPropagation(); setOpen(true); setEditing(true); } }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: canEditTitle ? 'text' : 'pointer', maxWidth: '100%' }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: '#101828', letterSpacing: -0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayTitle}</span>
              {typeof count === 'number' && count > 0 && (
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#6B7280', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 999, padding: '1px 7px', flexShrink: 0 }}>{count}</span>
              )}
              {canEditTitle && <Pen className="re-title-pen" size={12} color="#98A2B3" style={{ flexShrink: 0 }} />}
            </div>
          )}
          {isPremiumResolved && (
            <div title="This section is part of Premium. Upgrade to include it on your resume."
              style={{ fontSize: 9, fontWeight: 800, color: '#F97316', letterSpacing: '0.1em', marginTop: 3, cursor: 'help' }}>PREMIUM</div>
          )}
          {!open && !editing && summary && (
            <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</div>
          )}
          {!open && !editing && typeof count === 'number' && count === 0 && (
            <div style={{ fontSize: 11.5, color: '#C2C8D0', marginTop: 2, fontStyle: 'italic' }}>Empty — click to add</div>
          )}
        </div>

        {/* Up/down reorder buttons */}
        {(onMoveUp !== undefined || onMoveDown !== undefined) && (
          <div className="re-sec-move" onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
            <MoveBtn onClick={onMoveUp} disabled={!onMoveUp} up />
            <MoveBtn onClick={onMoveDown} disabled={!onMoveDown} />
          </div>
        )}

        {/* Chevron */}
        <ChevronDown size={18} color={open ? '#EF1D28' : '#9CA3AF'}
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
      </div>

      {/* Icon picker popup (fixed position to escape overflow:hidden) */}
      {showIconPicker && !noTitleEdit && pickerPos && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowIconPicker(false)} />
          <div style={{ position: 'fixed', top: pickerPos.top, left: pickerPos.left, width: 320, zIndex: 1000, background: 'white', border: '1.5px solid #E5E7EB', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', padding: 14 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Choose Icon</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ICON_OPTIONS.map(({ id, Icon: Ic }) => (
                <button key={id} onClick={() => { onIconChange(id); setShowIconPicker(false); }}
                  title={id}
                  style={{ width: 34, height: 34, background: sectionIcon === id ? '#FFF1E8' : 'white', border: sectionIcon === id ? '2px solid #EF1D28' : '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sectionIcon === id ? '#EF1D28' : '#374151' }}>
                  <Ic size={16} strokeWidth={1.8} />
                </button>
              ))}
              <button onClick={() => { onIconChange(''); setShowIconPicker(false); }}
                style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: sectionIcon === '' ? '#EF1D28' : '#6B7280', background: sectionIcon === '' ? '#FFF1E8' : 'white', border: sectionIcon === '' ? '1.5px solid #FDBA74' : '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', alignSelf: 'center' }}>
                None
              </button>
            </div>
          </div>
        </>
      )}

      {/* Body */}
      {open && (
        <div className="re-sec-body" ref={bodyRef} style={{ padding: '2px 18px 18px' }}>
          {/* Empty-state ghost card with guidance */}
          {typeof count === 'number' && count === 0 && onAdd && (
            <div style={{ border: '1.5px dashed #D1D5DB', borderRadius: 12, padding: '20px 16px', textAlign: 'center', margin: '10px 0 4px', background: '#FAFBFC' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#6B7280', lineHeight: 1.5 }}>{emptyHint || `Nothing here yet — add your first entry.`}</p>
              <button onClick={handleAdd} style={{ background: 'white', color: '#EF1D28', border: '1.5px solid #FECACA', borderRadius: 8, padding: '7px 18px', fontSize: 12.5, cursor: 'pointer', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {addLabel}
              </button>
            </div>
          )}
          {children}
          {/* Dashed add button at the bottom of the section */}
          {onAdd && !(typeof count === 'number' && count === 0) && (
            <button onClick={handleAdd}
              style={{ width: '100%', padding: '12px', border: '1.5px dashed #F5A3A8', borderRadius: 10, background: '#FFFBFB', color: '#EF1D28', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', marginTop: 6, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#FFF1F1'; e.currentTarget.style.borderColor = '#EF1D28'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#FFFBFB'; e.currentTarget.style.borderColor = '#F5A3A8'; }}>
              {addLabel === '+ Add' ? `+ Add ${displayTitle}` : addLabel}
            </button>
          )}
        </div>
      )}
    </div>
    </div>
  );
};

// ── Personal Info ─────────────────────────────────────────────────────────────
const PRESET_COLORS = [
  '#111827','#1F2937','#374151','#6B7280',
  '#EF1D28','#991B1B','#F97316','#0891B2',
  '#FB923C','#F97316','#EA580C','#FDBA74',
  '#059669','#047857','#10B981','#34D399',
  '#DC2626','#B91C1C','#EF4444','#F87171',
  '#D97706','#B45309','#F59E0B','#FBBF24',
  '#ffffff','#F9FAFB','#F3F4F6','#E5E7EB',
];

const ColorPicker = ({ label, value, onChange }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={{ ...lbl, marginBottom: 6 }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {PRESET_COLORS.map(c => (
        <div key={c} onClick={() => onChange(c)} style={{
          width: 20, height: 20, background: c, borderRadius: 4, cursor: 'pointer',
          border: value === c ? '3px solid #EF1D28' : '1.5px solid #D1D5DB',
          transform: value === c ? 'scale(1.2)' : 'scale(1)',
          transition: 'all 0.15s', flexShrink: 0,
        }} title={c} />
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input type="color" value={value || '#111827'} onChange={e => onChange(e.target.value)}
          style={{ width: 28, height: 28, border: '1.5px solid #D1D5DB', borderRadius: 4, cursor: 'pointer', padding: 1 }} />
        <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#000000"
          style={{ ...inp, width: 80, fontSize: 11, padding: '4px 8px' }} />
      </div>
    </div>
  </div>
);

const RE_PHOTO_TEMPLATES = new Set(['creative','elegant','corporate','blue_corporate','navy_timeline','blue_analyst','gregory_sidebar','academic','academic_modern','amber_executive','violet_sidebar','rose_gold','sky_pro','champagne']);
const RE_PHOTO_SHAPE_TEMPLATES = new Set(['corporate','blue_corporate','gregory_sidebar','academic_modern','amber_executive','violet_sidebar','rose_gold','sky_pro','champagne']);
const RE_PHOTO_SIZE_TEMPLATES = new Set(['corporate','blue_corporate','gregory_sidebar','academic_modern','navy_timeline','blue_analyst','amber_executive','violet_sidebar','rose_gold','sky_pro','champagne']);
const RE_DOB_TEMPLATES = new Set(['creative','corporate','blue_corporate','navy_timeline','harvard_standard','russian_classic','gregory_sidebar','academic','europass','academic_modern']);

const PersonalSection = ({ data, onChange, onImprove, aiLoading, template }) => {
  const pi = data;
  const set = (k, v) => onChange({ ...pi, [k]: v });
  const photoRef = useRef();
  const photoSize = pi.photoSize || 72;
  const photoShape = pi.photoShape || 'circle';
  const hasPhoto = RE_PHOTO_TEMPLATES.has(template);
  const hasPhotoShape = RE_PHOTO_SHAPE_TEMPLATES.has(template);
  const hasPhotoSize = RE_PHOTO_SIZE_TEMPLATES.has(template);
  const hasDOB = RE_DOB_TEMPLATES.has(template);

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => set('photo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const borderRadius = photoShape === 'circle' ? '50%' : photoShape === 'rounded' ? '12px' : '0px';

  return (
    <CollapsibleSection title="Personal Information" icon={User} analysisContent={{ ...pi, photo: undefined }}>
      <div style={{ display: 'flex', gap: 16, marginTop: 12, marginBottom: 14, alignItems: 'flex-start' }}>
        {hasPhoto && (
          <div style={{ flexShrink: 0, width: 110 }}>
            {/* Photo preview / upload zone */}
            <div onClick={() => photoRef.current?.click()} style={{
              width: 88, height: 88, borderRadius: photoShape === 'circle' ? '50%' : photoShape === 'rounded' ? 14 : 6,
              border: pi.photo ? '2.5px solid #E5E7EB' : '2px dashed #D1D5DB',
              cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', flexDirection: 'column',
              justifyContent: 'center', background: pi.photo ? 'transparent' : '#F9FAFB',
              margin: '0 auto', transition: 'border-color 0.15s', position: 'relative',
            }}
              onMouseEnter={e => { if (!pi.photo) e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.background = pi.photo ? 'rgba(0,0,0,0.04)' : '#FFF5F5'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = pi.photo ? '#E5E7EB' : '#D1D5DB'; e.currentTarget.style.background = pi.photo ? 'transparent' : '#F9FAFB'; }}>
              {pi.photo
                ? <img src={pi.photo} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <>
                    <Camera size={20} color="#9CA3AF" strokeWidth={1.5} />
                    <span style={{ fontSize: 9, color: '#9CA3AF', marginTop: 4, textAlign: 'center', lineHeight: 1.3, padding: '0 6px' }}>Add Photo</span>
                  </>
              }
            </div>

            <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />

            {/* Upload / Remove row */}
            <div style={{ display: 'flex', gap: 4, marginTop: 7, justifyContent: 'center' }}>
              <button onClick={() => photoRef.current?.click()} style={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#374151', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 5, padding: '4px 0', cursor: 'pointer' }}>
                {pi.photo ? 'Change' : 'Upload'}
              </button>
              {pi.photo && (
                <button onClick={() => set('photo', null)} style={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 5, padding: '4px 0', cursor: 'pointer' }}>
                  Remove
                </button>
              )}
            </div>

            {/* Shape selector */}
            {hasPhotoShape && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5, textAlign: 'center' }}>Shape</div>
                <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                  {[
                    { s: 'circle',  r: '50%',  label: 'Circle' },
                    { s: 'rounded', r: '5px',  label: 'Rounded' },
                    { s: 'square',  r: '0px',  label: 'Square' },
                  ].map(({ s, r, label }) => (
                    <button key={s} onClick={() => set('photoShape', s)} title={label} style={{
                      width: 28, height: 28, borderRadius: r, cursor: 'pointer',
                      background: photoShape === s ? '#EF1D28' : '#F3F4F6',
                      border: photoShape === s ? '2px solid #EF1D28' : '1.5px solid #D1D5DB',
                      transition: 'all 0.15s',
                    }} />
                  ))}
                </div>
              </div>
            )}

            {/* Size slider */}
            {hasPhotoSize && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#374151', background: '#F3F4F6', borderRadius: 4, padding: '1px 5px' }}>{photoSize}px</span>
                </div>
                <input type="range" min={40} max={160} value={photoSize} onChange={e => set('photoSize', parseInt(e.target.value))}
                  style={{ width: '100%', cursor: 'pointer', accentColor: '#EF1D28' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#C4C9D4', marginTop: 1 }}>
                  <span>S</span><span>L</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="re-grid-2" style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10  }}>
          {[['fullName','Full Name','span 2'],['jobTitle','Job Title / Target Role','span 2'],['email','Email'],['phone','Phone'],['location','Location'],['linkedin','LinkedIn URL'],['website','Website / Portfolio']].map(([k, l, span]) => (
            <div key={k} style={span ? { gridColumn: span } : {}}>
              <label style={lbl}>{l}</label>
              <input style={inp} value={pi[k] || ''} onChange={e => set(k, e.target.value)} placeholder={l} />
            </div>
          ))}
        </div>
      </div>

      {hasDOB && (
        <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 8 }}>Optional Identity Fields</div>
          <div className="re-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lbl}>Date of Birth</label>
              <input type="date" style={inp} value={pi.dateOfBirth || ''} onChange={e => set('dateOfBirth', e.target.value)} />
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!pi.showDOB} onChange={e => set('showDOB', e.target.checked)} /> Show on CV
              </label>
            </div>
            <div>
              <label style={lbl}>Nationality</label>
              <input style={inp} value={pi.nationality || ''} onChange={e => set('nationality', e.target.value)} placeholder="e.g. British" />
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!pi.showNationality} onChange={e => set('showNationality', e.target.checked)} /> Show on CV
              </label>
            </div>
            <div>
              <label style={lbl}>Sex</label>
              <select style={inp} value={pi.sex || ''} onChange={e => set('sex', e.target.value)}>
                <option value="">Select</option>
                <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
              </select>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!pi.showSex} onChange={e => set('showSex', e.target.checked)} /> Show on CV
              </label>
            </div>
          </div>
        </div>
      )}

      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <input style={{ ...inp, flex: '1 1 120px', minWidth: 0, fontWeight: 600 }} value={pi.summaryTitle || 'Professional Summary'} onChange={e => set('summaryTitle', e.target.value)} placeholder="Section title (e.g. About Me)" />
          <AIBtn onClick={() => onImprove('summary', pi.summary)} loading={aiLoading}>Improve</AIBtn>
        </div>
        <SmartTextarea value={pi.summary || ''} onChange={v => set('summary', v)} placeholder="Write a compelling professional summary..." rows={4} />
        {(() => {
          const len = (pi.summary || '').length;
          const status = len === 0 ? null : len < 200 ? ['A bit short — aim for 2–4 sentences (300–600 characters)', '#D97706'] : len <= 700 ? ['Good length', '#059669'] : ['Getting long — recruiters skim; consider trimming', '#D97706'];
          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11 }}>
              <span style={{ color: status ? status[1] : '#9CA3AF' }}>{status ? status[0] : 'Tip: 2–4 sentences covering role, years of experience and one standout result'}</span>
              <span style={{ color: '#9CA3AF', flexShrink: 0, marginLeft: 8 }}>{len} chars</span>
            </div>
          );
        })()}
      </div>
    </CollapsibleSection>
  );
};

// ── Experience ────────────────────────────────────────────────────────────────
const ExperienceSection = ({ items, onChange, onGenerateBullets, onImproveDescription, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), company: '', title: '', location: '', startDate: '', endDate: '', current: false, description: '', bullets: [''] }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));

  return (
    <CollapsibleSection title="Work Experience" icon={Briefcase} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="experience" count={items.length} analysisContent={items} summary={items.map(i => [i.title, i.company].filter(Boolean).join(' @ ')).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Add your most recent role first — recruiters read top-down. Include 2–4 bullet points with measurable impact.">
      {items.map((exp, n) => (
        <EntryCard key={exp.id} label={`Work Experience #${n + 1}`} onRemove={() => rm(exp.id)} onMoveUp={n > 0 ? () => move(exp.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(exp.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Company</label><input style={inp} value={exp.company || ''} onChange={e => upd(exp.id, 'company', e.target.value)} placeholder="Company name" /></div>
            <div><label style={lbl}>Job Title</label><input style={inp} value={exp.title || ''} onChange={e => upd(exp.id, 'title', e.target.value)} placeholder="e.g. Project Manager" /></div>
            <div><label style={lbl}>Location</label><input style={inp} value={exp.location || ''} onChange={e => upd(exp.id, 'location', e.target.value)} placeholder="City, Country" /></div>
            <div><label style={lbl}>Start Date</label><input type="month" style={inp} value={exp.startDate || ''} onChange={e => upd(exp.id, 'startDate', e.target.value)} /></div>
            <div>
              <label style={lbl}>End Date</label>
              <input type="month" style={inp} value={exp.endDate || ''} onChange={e => upd(exp.id, 'endDate', e.target.value)} disabled={exp.current} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 10 }}>
              <label style={{ display: 'flex', gap: 7, fontSize: 13, cursor: 'pointer', alignItems: 'center', color: '#374151' }}>
                <input type="checkbox" checked={!!exp.current} onChange={e => upd(exp.id, 'current', e.target.checked)} /> I currently work here
              </label>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Description</label>
              <AIBtn onClick={() => onImproveDescription(exp.id, exp.description)} loading={aiLoading}>Improve</AIBtn>
            </div>
            <SmartTextarea value={exp.description || ''} onChange={v => upd(exp.id, 'description', v)} placeholder="Brief overview of your role and responsibilities..." rows={2} />
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={lbl}>Key Achievements / Bullet Points</label>
              <AIBtn onClick={() => onGenerateBullets(exp.id, exp.title, exp.company, exp.description)} loading={aiLoading}>Generate with AI</AIBtn>
            </div>
            <BulletEditor bullets={exp.bullets} onChange={bullets => upd(exp.id, 'bullets', bullets)} />
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Education ─────────────────────────────────────────────────────────────────
const EducationSection = ({ items, onChange, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), school: '', degree: '', location: '', startDate: '', endDate: '', gpa: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Education" icon={GraduationCap} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="education" count={items.length} analysisContent={items} summary={items.map(i => i.school || i.degree).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Add your highest qualification first. GPA is optional — include it if it strengthens your application.">
      {items.map((edu, n) => (
        <EntryCard key={edu.id} label={`Education #${n + 1}`} onRemove={() => rm(edu.id)} onMoveUp={n > 0 ? () => move(edu.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(edu.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['school','School / University','span 2'],['degree','Degree / Field of Study','span 2'],['location','Location'],['gpa','GPA (optional)']].map(([k, l, span]) => (
              <div key={k} style={span ? { gridColumn: span } : {}}><label style={lbl}>{l}</label><input style={inp} value={edu[k] || ''} onChange={e => upd(edu.id, k, e.target.value)} /></div>
            ))}
            <div><label style={lbl}>Start</label><input type="month" style={inp} value={edu.startDate || ''} onChange={e => upd(edu.id, 'startDate', e.target.value)} /></div>
            <div><label style={lbl}>End</label><input type="month" style={inp} value={edu.endDate || ''} onChange={e => upd(edu.id, 'endDate', e.target.value)} /></div>
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Skills ────────────────────────────────────────────────────────────────────
const AIJobPrompt = ({ onGenerate, aiLoading, placeholder = 'e.g. Software Engineer' }) => {
  const [show, setShow] = useLocalState(false);
  const [jobTitle, setJobTitle] = useLocalState('');
  const handle = async () => {
    if (!jobTitle.trim()) return;
    await onGenerate(jobTitle.trim());
    setShow(false);
    setJobTitle('');
  };
  if (!show) return (
    <AIBtn onClick={() => setShow(true)} loading={false}>Write with AI</AIBtn>
  );
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#FFF8F1', border: '1px solid #FDBA74', borderRadius: 8, padding: '7px 10px', marginTop: 8 }}>
      <Sparkles size={13} color="#F97316" style={{ flexShrink: 0 }} />
      <input
        autoFocus
        value={jobTitle}
        onChange={e => setJobTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handle(); if (e.key === 'Escape') { setShow(false); setJobTitle(''); } }}
        placeholder={placeholder}
        style={{ ...inp, flex: 1, padding: '5px 8px', fontSize: 12, background: 'white' }}
      />
      <button onClick={handle} disabled={aiLoading || !jobTitle.trim()} style={{ background: aiLoading ? '#E5E7EB' : 'linear-gradient(135deg,#FB923C,#F97316)', color: aiLoading ? '#9CA3AF' : 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: aiLoading ? 'not-allowed' : 'pointer', flexShrink: 0, fontFamily: 'Inter,sans-serif' }}>
        {aiLoading ? '...' : 'Generate'}
      </button>
      <button onClick={() => { setShow(false); setJobTitle(''); }} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
    </div>
  );
};

const SkillsSection = ({ items, onChange, onGenerateSkills, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange, template }) => {
  const isSkillBar = template === 'gregory_sidebar';
  const add = () => onChange([...items, { id: Date.now().toString(), category: '', items: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Skills" icon={Zap} onAdd={add} addLabel={isSkillBar ? '+ Add Skill' : '+ Add Category'} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="skills" count={items.length} analysisContent={items} summary={items.map(i => i.category || i.items).filter(Boolean).slice(0, 4).join(' · ')} emptyHint="Group skills into categories (e.g. Frontend, Backend, Tools) — or let AI suggest them from your job title above.">
      <div style={{ marginTop: 8 }}>
        <AIJobPrompt onGenerate={onGenerateSkills} aiLoading={aiLoading} placeholder="Enter job title, e.g. Frontend Developer" />
      </div>
      {items.map(s => (
        <div key={s.id} style={{ marginBottom: 10, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: isSkillBar ? 6 : 0 }}>
            {!isSkillBar && (
              <input style={{ ...inp, width: 130, flexShrink: 0 }} value={s.category || ''} onChange={e => upd(s.id, 'category', e.target.value)} placeholder="Category" />
            )}
            <input
              style={isSkillBar ? { ...inp, flex: 1 } : inp}
              value={isSkillBar ? (s.category || '') : (s.items || '')}
              onChange={e => upd(s.id, isSkillBar ? 'category' : 'items', e.target.value)}
              placeholder={isSkillBar ? 'Skill name, e.g. JavaScript' : 'skill1, skill2, skill3...'}
            />
            <button className="re-x" onClick={() => rm(s.id)} title="Remove">×</button>
          </div>
          {isSkillBar && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 2 }}>
              <span style={{ fontSize: 11, color: '#6B7280', flexShrink: 0 }}>Level</span>
              <input
                type="range" min="1" max="5" step="1"
                value={s.level ?? 4}
                onChange={e => upd(s.id, 'level', Number(e.target.value))}
                style={{ flex: 1, accentColor: '#1B2A4A', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, width: 12, textAlign: 'center', flexShrink: 0 }}>{s.level ?? 4}</span>
            </div>
          )}
        </div>
      ))}
    </CollapsibleSection>
  );
};

// ── Hobbies ───────────────────────────────────────────────────────────────────
const HobbiesSection = ({ value, onChange, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const tags = (value || '').split(',').map(h => h.trim()).filter(Boolean);
  return (
    <CollapsibleSection title="Hobbies & Interests" icon={Heart} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="hobbies" analysisContent={value || ''} summary={(value || '').slice(0, 90)}>
      <div style={{ marginTop: 10 }}>
        <input style={inp} value={value || ''} onChange={e => onChange(e.target.value)} placeholder="e.g. Photography, Hiking, Reading, Chess, Traveling..." />
        {tags.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tags.map((h, i) => (
              <span key={i} style={{ background: '#FFF1E8', color: '#C5161D', borderRadius: 20, padding: '3px 11px', fontSize: 12, fontWeight: 600 }}>{h}</span>
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ── Key Strengths ─────────────────────────────────────────────────────────────
const KeyStrengthsSection = ({ value, onChange, onGenerateStrengths, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const tags = (value || '').split(',').map(h => h.trim()).filter(Boolean);
  return (
    <CollapsibleSection title="Key Strengths" icon={Star} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="strengths" analysisContent={value || ''} summary={(value || '').slice(0, 90)}>
      <div style={{ marginTop: 8 }}>
        <AIJobPrompt onGenerate={onGenerateStrengths} aiLoading={aiLoading} placeholder="Enter job title, e.g. Product Manager" />
      </div>
      <div style={{ marginTop: 10 }}>
        <input style={inp} value={value || ''} onChange={e => onChange(e.target.value)} placeholder="e.g. Leadership, Problem Solving, Communication, Adaptability..." />
        {tags.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tags.map((h, i) => (
              <span key={i} style={{ background: '#FFF1E8', color: '#C5161D', borderRadius: 20, padding: '3px 11px', fontSize: 12, fontWeight: 600 }}>{h}</span>
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ── Goals ─────────────────────────────────────────────────────────────────────
const GoalsSection = ({ value, onChange, onImprove, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => (
  <CollapsibleSection title="Career Goals" icon={Target} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="goals" analysisContent={value || ''} summary={(value || '').slice(0, 90)}>
    <div style={{ marginTop: 10 }}>
      <LabelWithImprove onImprove={() => onImprove(value)} loading={aiLoading}>Goals</LabelWithImprove>
      <SmartTextarea value={value || ''} onChange={onChange} placeholder="e.g. Secure a senior engineering role at a product-led company, lead cross-functional teams, and contribute to impactful open-source projects..." rows={3} />
    </div>
  </CollapsibleSection>
);

// ── Projects ──────────────────────────────────────────────────────────────────
const ProjectsSection = ({ items, onChange, onImproveField, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), title: '', date: '', summary: '', bullets: [], link: '', skills: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Projects" icon={Code2} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="projects" count={items.length} analysisContent={items} summary={items.map(i => i.title).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Showcase 1–3 projects that prove your skills — side projects and open source count too.">
      {items.map((p, n) => (
        <EntryCard key={p.id} label={p.title ? `${p.title}` : `Project #${n + 1}`} onRemove={() => rm(p.id)} onMoveUp={n > 0 ? () => move(p.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(p.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Project Title</label><input style={inp} value={p.title || ''} onChange={e => upd(p.id, 'title', e.target.value)} /></div>
            <div><label style={lbl}>Date</label><input type="month" style={inp} value={p.date || ''} onChange={e => upd(p.id, 'date', e.target.value)} /></div>
            <div style={{ gridColumn: 'span 2' }}><LabelWithImprove onImprove={() => onImproveField('projects', p.id, 'summary', p.summary)} loading={aiLoading}>Summary</LabelWithImprove><SmartTextarea value={p.summary || ''} onChange={v => upd(p.id, 'summary', v)} rows={2} /></div>
            <div><label style={lbl}>Project Link (URL)</label><input style={inp} value={p.link || ''} onChange={e => upd(p.id, 'link', e.target.value)} placeholder="https://..." /></div>
            <div><label style={lbl}>Skills / Tech Used</label><input style={inp} value={p.skills || ''} onChange={e => upd(p.id, 'skills', e.target.value)} placeholder="React, Node.js, AWS..." /></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Bullet Points</label>
            <BulletEditor bullets={p.bullets} onChange={bullets => upd(p.id, 'bullets', bullets)} placeholder="Key feature or achievement..." />
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Certifications ────────────────────────────────────────────────────────────
const CertSection = ({ items, onChange, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), name: '', issuer: '', date: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Certifications" icon={Award} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="certifications" count={items.length} analysisContent={items} summary={items.map(i => i.name).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="List industry certifications with the issuing organisation and date.">
      {items.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 8, marginTop: 10 }}>
          <input style={inp} value={c.name || ''} onChange={e => upd(c.id, 'name', e.target.value)} placeholder="Certification name" />
          <input style={{ ...inp, width: 140 }} value={c.issuer || ''} onChange={e => upd(c.id, 'issuer', e.target.value)} placeholder="Issuer" />
          <input type="month" style={{ ...inp, width: 130 }} value={c.date || ''} onChange={e => upd(c.id, 'date', e.target.value)} />
          <button className="re-x" onClick={() => rm(c.id)} title="Remove">×</button>
        </div>
      ))}
    </CollapsibleSection>
  );
};

// ── Achievements ──────────────────────────────────────────────────────────────
const AchievementsSection = ({ items, onChange, onImproveField, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), title: '', description: '', date: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Achievements" icon={Trophy} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="achievements" count={items.length} analysisContent={items} summary={items.map(i => i.title).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Highlight wins that set you apart — promotions, records, recognitions.">
      {items.map((a, n) => (
        <EntryCard key={a.id} label={a.title || `Achievement #${n + 1}`} onRemove={() => rm(a.id)} onMoveUp={n > 0 ? () => move(a.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(a.id, 1) : undefined}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 10 }}>
            <input style={inp} value={a.title || ''} onChange={e => upd(a.id, 'title', e.target.value)} placeholder="Achievement title" />
            <input type="month" style={{ ...inp, width: 150 }} value={a.date || ''} onChange={e => upd(a.id, 'date', e.target.value)} />
          </div>
          <LabelWithImprove onImprove={() => onImproveField('achievements', a.id, 'description', a.description)} loading={aiLoading}>Description</LabelWithImprove>
          <SmartTextarea value={a.description || ''} onChange={v => upd(a.id, 'description', v)} placeholder="Describe the achievement and its impact..." rows={2} />
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Languages ─────────────────────────────────────────────────────────────────
const LanguagesSection = ({ items, onChange, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), language: '', proficiency: 'Conversational' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Languages" icon={Globe} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="languages" count={items.length} analysisContent={items} summary={items.map(i => i.language).filter(Boolean).slice(0, 4).join(' · ')} emptyHint="Add languages with proficiency levels (Native, Fluent, Conversational).">
      {items.map(l => (
        <div key={l.id} style={{ display: 'flex', gap: 8, marginBottom: 8, marginTop: 10 }}>
          <input style={inp} value={l.language || ''} onChange={e => upd(l.id, 'language', e.target.value)} placeholder="Language" />
          <select style={{ ...inp, width: 180 }} value={l.proficiency || ''} onChange={e => upd(l.id, 'proficiency', e.target.value)}>
            {['Native','Fluent','Professional','Conversational','Basic'].map(p => <option key={p}>{p}</option>)}
          </select>
          <button className="re-x" onClick={() => rm(l.id)} title="Remove">×</button>
        </div>
      ))}
    </CollapsibleSection>
  );
};

// ── Custom Blocks ─────────────────────────────────────────────────────────────
const CustomBlocksSection = ({ items, onChange, onImproveField, aiLoading, onMoveUp, onMoveDown, dragHandle }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), title: 'Custom Section', description: '', link: '', date: '', bullets: [], showLink: false, showDate: false, showBullets: false }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));

  return (
    <CollapsibleSection title="Custom Blocks" icon={Layers} onAdd={add} addLabel="+ Add Block" dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} noTitleEdit={true} sectionKey="customBlocks" count={items.length} summary={items.map(i => i.title).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Create your own section — e.g. Conferences, Patents, Speaking engagements.">
      <p style={{ margin: '8px 0 4px', fontSize: 12, color: '#9CA3AF' }}>Add any section — Volunteer Work, Awards, Publications, References, etc.</p>
      {items.map((blk, n) => (
        <EntryCard key={blk.id} label={blk.title || `Custom Block #${n + 1}`} onRemove={() => rm(blk.id)} onMoveUp={n > 0 ? () => move(blk.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(blk.id, 1) : undefined}>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Section Title</label>
            <input style={{ ...inp, fontWeight: 600 }} value={blk.title || ''} onChange={e => upd(blk.id, 'title', e.target.value)} placeholder="Section Title (e.g. Volunteer Work)" />
          </div>
          <LabelWithImprove onImprove={() => onImproveField('customBlocks', blk.id, 'description', blk.description)} loading={aiLoading}>Description</LabelWithImprove>
          <SmartTextarea value={blk.description || ''} onChange={v => upd(blk.id, 'description', v)} placeholder="Description text..." rows={2} />
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            {[['showDate','Include Date'],['showLink','Include Link'],['showBullets','Include Bullets']].map(([k, label]) => (
              <label key={k} style={{ display: 'flex', gap: 4, fontSize: 12, cursor: 'pointer', alignItems: 'center', color: '#374151' }}>
                <input type="checkbox" checked={!!blk[k]} onChange={e => upd(blk.id, k, e.target.checked)} /> {label}
              </label>
            ))}
          </div>
          {blk.showDate && <div style={{ marginTop: 8 }}><label style={lbl}>Date</label><input type="month" style={{ ...inp, width: 160 }} value={blk.date || ''} onChange={e => upd(blk.id, 'date', e.target.value)} /></div>}
          {blk.showLink && <div style={{ marginTop: 8 }}><label style={lbl}>Link</label><input style={inp} value={blk.link || ''} onChange={e => upd(blk.id, 'link', e.target.value)} placeholder="https://..." /></div>}
          {blk.showBullets && (
            <div style={{ marginTop: 8 }}>
              <label style={lbl}>Bullet Points</label>
              <BulletEditor bullets={blk.bullets} onChange={bullets => upd(blk.id, 'bullets', bullets)} placeholder="Point..." accent="#FB923C" />
            </div>
          )}
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Publications (CV only) ─────────────────────────────────────────────────────
const PublicationsSection = ({ items, onChange, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), title: '', venue: '', year: '', authors: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Publications" icon={BookOpen} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="publications" count={items.length} analysisContent={items} summary={items.map(i => i.title).filter(Boolean).slice(0, 2).join(' · ')} emptyHint="Cite papers, articles or books — authors, year, title and venue.">
      {items.map((p, n) => (
        <EntryCard key={p.id} label={`Publication #${n + 1}`} onRemove={() => rm(p.id)} onMoveUp={n > 0 ? () => move(p.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(p.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Title</label><input style={inp} value={p.title || ''} onChange={e => upd(p.id, 'title', e.target.value)} /></div>
            <div><label style={lbl}>Venue / Journal</label><input style={inp} value={p.venue || ''} onChange={e => upd(p.id, 'venue', e.target.value)} /></div>
            <div><label style={lbl}>Year</label><input style={inp} value={p.year || ''} onChange={e => upd(p.id, 'year', e.target.value)} placeholder="2024" /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Authors</label><input style={inp} value={p.authors || ''} onChange={e => upd(p.id, 'authors', e.target.value)} /></div>
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Awards ────────────────────────────────────────────────────────────────────
const AwardsSection = ({ items, onChange, onImproveField, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), title: '', issuer: '', date: '', description: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Awards" icon={Medal} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="awards" count={items.length} analysisContent={items} summary={items.map(i => i.title).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Add honours and awards with the issuer and date.">
      {items.map((a, n) => (
        <EntryCard key={a.id} label={a.title || `Award #${n + 1}`} onRemove={() => rm(a.id)} onMoveUp={n > 0 ? () => move(a.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(a.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Award Title</label><input style={inp} value={a.title || ''} onChange={e => upd(a.id, 'title', e.target.value)} placeholder="e.g. Employee of the Year" /></div>
            <div><label style={lbl}>Issuing Organization</label><input style={inp} value={a.issuer || ''} onChange={e => upd(a.id, 'issuer', e.target.value)} placeholder="e.g. Google" /></div>
            <div><label style={lbl}>Date</label><input type="month" style={inp} value={a.date || ''} onChange={e => upd(a.id, 'date', e.target.value)} /></div>
            <div style={{ gridColumn: 'span 2' }}>
              <LabelWithImprove onImprove={() => onImproveField('awards', a.id, 'description', a.description)} loading={aiLoading}>Description (optional)</LabelWithImprove>
              <SmartTextarea value={a.description || ''} onChange={v => upd(a.id, 'description', v)} placeholder="Brief description of the award..." rows={2} />
            </div>
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── Volunteering ──────────────────────────────────────────────────────────────
const VolunteeringSection = ({ items, onChange, onImproveField, aiLoading, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), organization: '', role: '', location: '', startDate: '', endDate: '', current: false, description: '', bullets: [] }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="Volunteering" icon={HeartHandshake} onAdd={add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="volunteering" count={items.length} analysisContent={items} summary={items.map(i => i.organization || i.role).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Volunteer work shows initiative — add the role, organisation and what you contributed.">
      {items.map((v, n) => (
        <EntryCard key={v.id} label={`Volunteering #${n + 1}`} onRemove={() => rm(v.id)} onMoveUp={n > 0 ? () => move(v.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(v.id, 1) : undefined}>
          <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Role / Position</label><input style={inp} value={v.role || ''} onChange={e => upd(v.id, 'role', e.target.value)} placeholder="e.g. Team Coordinator" /></div>
            <div><label style={lbl}>Organization</label><input style={inp} value={v.organization || ''} onChange={e => upd(v.id, 'organization', e.target.value)} placeholder="e.g. Red Cross" /></div>
            <div><label style={lbl}>Location</label><input style={inp} value={v.location || ''} onChange={e => upd(v.id, 'location', e.target.value)} /></div>
            <div>
              <label style={lbl}>Start Date</label>
              <input type="month" style={inp} value={v.startDate || ''} onChange={e => upd(v.id, 'startDate', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>End Date</label>
              <input type="month" style={inp} value={v.endDate || ''} onChange={e => upd(v.id, 'endDate', e.target.value)} disabled={v.current} />
              <label style={{ display: 'flex', gap: 4, fontSize: 11, marginTop: 4, cursor: 'pointer', alignItems: 'center' }}>
                <input type="checkbox" checked={!!v.current} onChange={e => upd(v.id, 'current', e.target.checked)} /> Ongoing
              </label>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <LabelWithImprove onImprove={() => onImproveField('volunteering', v.id, 'description', v.description)} loading={aiLoading}>Description</LabelWithImprove>
            <SmartTextarea value={v.description || ''} onChange={val => upd(v.id, 'description', val)} placeholder="Describe your volunteer activities..." rows={2} />
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={lbl}>Bullet Points</label>
            <BulletEditor bullets={v.bullets} onChange={bullets => upd(v.id, 'bullets', bullets)} placeholder="Key contribution or achievement..." />
          </div>
        </EntryCard>
      ))}
    </CollapsibleSection>
  );
};

// ── References ────────────────────────────────────────────────────────────────
const ReferencesSection = ({ items, onChange, showAvailableOnRequest, onToggleAvailableOnRequest, onMoveUp, onMoveDown, dragHandle, sectionTitle, onTitleChange, sectionIcon, onIconChange }) => {
  const add = () => onChange([...items, { id: Date.now().toString(), name: '', title: '', company: '', email: '', phone: '', relationship: '' }]);
  const rm = id => onChange(items.filter(i => i.id !== id));
  const move = (id, dir) => { const idx = items.findIndex(i => i.id === id), nx = idx + dir; if (nx < 0 || nx >= items.length) return; const a = [...items]; [a[idx], a[nx]] = [a[nx], a[idx]]; onChange(a); };
  const upd = (id, k, v) => onChange(items.map(i => i.id === id ? { ...i, [k]: v } : i));
  return (
    <CollapsibleSection title="References" icon={UserCheck} onAdd={showAvailableOnRequest ? undefined : add} dragHandle={dragHandle} onMoveUp={onMoveUp} onMoveDown={onMoveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} sectionKey="references" count={showAvailableOnRequest ? undefined : items.length} analysisContent={showAvailableOnRequest ? 'Available upon request' : items} summary={showAvailableOnRequest ? 'Available upon request' : items.map(i => i.name).filter(Boolean).slice(0, 3).join(' · ')} emptyHint="Add 2–3 professional references, or toggle ‘Available upon request’ below.">
      <div style={{ marginTop: 10 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, cursor: 'pointer', color: '#374151', marginBottom: 12 }}>
          <input type="checkbox" checked={!!showAvailableOnRequest} onChange={e => onToggleAvailableOnRequest(e.target.checked)} />
          <span>Show "References available upon request" only</span>
        </label>
      </div>
      {!showAvailableOnRequest && (
        <>
          {items.map((r, n) => (
            <EntryCard key={r.id} label={r.name || `Reference #${n + 1}`} onRemove={() => rm(r.id)} onMoveUp={n > 0 ? () => move(r.id, -1) : undefined} onMoveDown={n < items.length - 1 ? () => move(r.id, 1) : undefined}>
              <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={lbl}>Full Name</label><input style={inp} value={r.name || ''} onChange={e => upd(r.id, 'name', e.target.value)} placeholder="e.g. Jane Smith" /></div>
                <div><label style={lbl}>Relationship</label><input style={inp} value={r.relationship || ''} onChange={e => upd(r.id, 'relationship', e.target.value)} placeholder="e.g. Former Manager" /></div>
                <div><label style={lbl}>Job Title</label><input style={inp} value={r.title || ''} onChange={e => upd(r.id, 'title', e.target.value)} placeholder="e.g. Engineering Lead" /></div>
                <div><label style={lbl}>Company</label><input style={inp} value={r.company || ''} onChange={e => upd(r.id, 'company', e.target.value)} /></div>
                <div><label style={lbl}>Email</label><input type="email" style={inp} value={r.email || ''} onChange={e => upd(r.id, 'email', e.target.value)} /></div>
                <div><label style={lbl}>Phone</label><input style={inp} value={r.phone || ''} onChange={e => upd(r.id, 'phone', e.target.value)} /></div>
              </div>
            </EntryCard>
          ))}
        </>
      )}
    </CollapsibleSection>
  );
};

// ── Cover Letter Editor ───────────────────────────────────────────────────────
const SALUTATION_OPTIONS = ['Best Regards,', 'Sincerely,', 'Kind Regards,', 'Yours Faithfully,', 'Warm Regards,', 'Respectfully,', 'With Appreciation,'];

const CoverLetterEditor = ({ data, onChange, onImprove, aiLoading }) => {
  const pi = data.personalInfo || {};
  const ji = data.jobInfo || {};
  const co = data.content || {};
  const sig = data.signature || { type: 'cursive', image: null };
  const salutation = data.closingSalutation || 'Best Regards,';
  const sigFileRef = React.useRef();

  const setPI = v => onChange({ ...data, personalInfo: v });
  const setJI = v => onChange({ ...data, jobInfo: v });
  const setCO = v => onChange({ ...data, content: v });
  const setTemplate = t => onChange({ ...data, template: t });
  const setSig = v => onChange({ ...data, signature: { ...sig, ...v } });
  const setSalutation = v => onChange({ ...data, closingSalutation: v });

  const handleSigUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSig({ type: 'uploaded', image: ev.target.result });
    reader.readAsDataURL(file);
  };

  return (
    <div>
      {/* Template Picker */}
      <div style={{ marginBottom: 16, padding: '14px 16px', background: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Template Style</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CL_TEMPLATES.map(t => {
            const active = (data.template || 'cl_classic') === t.id;
            return (
              <button key={t.id} onClick={() => setTemplate(t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 8, border: `2px solid ${active ? t.color : '#E5E7EB'}`, background: active ? `${t.color}10` : 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: active ? 700 : 500, color: active ? t.color : '#6B7280', transition: 'all 0.15s' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                {t.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Accent Color */}
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Accent Color</div>
        <input type="color" value={pi.accentColor || (CL_TEMPLATES.find(t => t.id === data.template)?.color || '#1E3A5F')}
          onChange={e => setPI({ ...pi, accentColor: e.target.value })}
          style={{ width: 36, height: 28, borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer', padding: 2, background: 'white' }} />
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>Customize the template color</span>
      </div>

      <CollapsibleSection title="Your Information" icon={User} sectionKey="personalInfo">
        <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10  }}>
          {[['fullName','Full Name'],['jobTitle','Job Title'],['email','Email'],['phone','Phone'],['location','Location'],['website','Website']].map(([k, l]) => (
            <div key={k}><label style={lbl}>{l}</label><input style={inp} value={pi[k] || ''} onChange={e => setPI({ ...pi, [k]: e.target.value })} /></div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Job Information" icon={Briefcase} sectionKey="jobInfo">
        <div className="re-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10  }}>
          {[['company','Company'],['role','Role / Position'],['hiringManager','Hiring Manager']].map(([k, l]) => (
            <div key={k} style={k === 'company' ? { gridColumn: 'span 2' } : {}}><label style={lbl}>{l}</label><input style={inp} value={ji[k] || ''} onChange={e => setJI({ ...ji, [k]: e.target.value })} /></div>
          ))}
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Letter Date</label>
            <input type="date" style={inp} value={data.letterDate || ''} onChange={e => onChange({ ...data, letterDate: e.target.value })} />
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave blank to hide the date</div>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Opening Greeting</label>
            <input style={inp} value={data.openingGreeting || ''} placeholder={`e.g. Dear ${ji.hiringManager ? `Ms. ${ji.hiringManager.split(' ').pop()}` : 'Hiring Manager'},`} onChange={e => onChange({ ...data, openingGreeting: e.target.value })} />
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave blank to auto-generate from Hiring Manager name</div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Letter Content" icon={BookOpen} sectionKey="content">
        <div style={{ marginTop: 10 }}>
          {[['opening','Opening Paragraph',2],['body','Body (2 paragraphs)',4],['closing','Closing Paragraph',2]].map(([k, l, r]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={lbl}>{l}</label>
                <AIBtn onClick={() => onImprove(k, co[k])} loading={aiLoading}>Improve</AIBtn>
              </div>
              <SmartTextarea value={co[k] || ''} onChange={v => setCO({ ...co, [k]: v })} rows={r} />
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Closing Salutation */}
      <CollapsibleSection title="Closing & Signature" icon={Pen} sectionKey="signature">
        <div style={{ marginTop: 12 }}>
          <label style={lbl}>Closing Salutation</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {SALUTATION_OPTIONS.map(s => (
              <button key={s} onClick={() => setSalutation(s)}
                style={{ padding: '5px 12px', borderRadius: 8, border: `1.5px solid ${salutation === s ? '#EF1D28' : '#E5E7EB'}`, background: salutation === s ? '#FFF1F2' : 'white', color: salutation === s ? '#DC2626' : '#6B7280', fontSize: 12, fontWeight: salutation === s ? 700 : 400, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                {s}
              </button>
            ))}
          </div>
          <input style={{ ...inp, marginTop: 4 }} value={salutation} onChange={e => setSalutation(e.target.value)} placeholder="Or type a custom closing..." />

          <div style={{ marginTop: 18 }}>
            <label style={lbl}>Signature Style</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[
                { id: 'cursive', label: 'Cursive (Auto)' },
                { id: 'uploaded', label: 'Upload Image' },
                { id: 'none', label: 'None' },
              ].map(opt => (
                <button key={opt.id} onClick={() => setSig({ type: opt.id })}
                  style={{ flex: 1, padding: '8px', borderRadius: 9, border: `2px solid ${sig.type === opt.id ? '#EF1D28' : '#E5E7EB'}`, background: sig.type === opt.id ? '#FFF1F2' : 'white', color: sig.type === opt.id ? '#DC2626' : '#6B7280', fontSize: 12, fontWeight: sig.type === opt.id ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {sig.type === 'cursive' && (
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Preview</div>
                <div style={{ fontFamily: "'Dancing Script', cursive", fontSize: 22, color: pi.accentColor || '#1E3A5F', lineHeight: 1.2 }}>{pi.fullName || 'Your Name'}</div>
              </div>
            )}

            {sig.type === 'uploaded' && (
              <div>
                <input ref={sigFileRef} type="file" accept="image/*" onChange={handleSigUpload} style={{ display: 'none' }} />
                <button onClick={() => sigFileRef.current?.click()}
                  style={{ width: '100%', padding: '10px', border: '2px dashed #E5E7EB', borderRadius: 10, background: '#F9FAFB', color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                  {sig.image ? '🖼 Change signature image' : '⬆ Upload signature image (PNG/JPG)'}
                </button>
                {sig.image && (
                  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <img src={sig.image} alt="Signature" style={{ maxHeight: 50, maxWidth: 180, objectFit: 'contain' }} />
                    <button onClick={() => setSig({ image: null, type: 'cursive' })}
                      style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}

            {sig.type === 'none' && (
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 18px', fontSize: 12, color: '#9CA3AF' }}>
                No signature will appear — only your printed name.
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

// ── Default section ordering ──────────────────────────────────────────────────
const DEFAULT_ORDER = ['experience', 'education', 'skills', 'projects', 'strengths', 'goals', 'hobbies', 'achievements', 'certifications', 'awards', 'languages', 'volunteering', 'publications', 'references', 'customBlocks'];
const CV_ORDER = ['experience', 'education', 'publications', 'skills', 'projects', 'strengths', 'goals', 'hobbies', 'achievements', 'certifications', 'awards', 'languages', 'volunteering', 'references', 'customBlocks'];

// ── Sortable section wrapper ──────────────────────────────────────────────────
const SortableSectionItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  };
  const dragHandle = { ...listeners, ...attributes };
  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children, { dragHandle })}
    </div>
  );
};

// ── Sticky editor nav: completion meter + section jump chips ─────────────────
const NAV_LABELS = { experience: 'Experience', education: 'Education', skills: 'Skills', projects: 'Projects', strengths: 'Strengths', goals: 'Goals', hobbies: 'Hobbies', achievements: 'Achievements', certifications: 'Certifications', awards: 'Awards', languages: 'Languages', volunteering: 'Volunteering', publications: 'Publications', references: 'References', customBlocks: 'Custom' };

const EditorNav = ({ data, sectionOrder }) => {
  const pi = data.personalInfo || {};
  const checks = [
    [!!pi.fullName, 'your name', 10],
    [!!pi.email, 'an email address', 8],
    [!!pi.jobTitle, 'a target role', 8],
    [!!pi.summary, 'a professional summary', 16],
    [(data.experience || []).length > 0, 'work experience', 22],
    [(data.education || []).length > 0, 'education', 14],
    [(data.skills || []).length > 0, 'skills', 14],
    [(data.projects || []).length > 0 || (data.certifications || []).length > 0, 'a project or certification', 8],
  ];
  const total = checks.reduce((a, [, , w]) => a + w, 0);
  const got = checks.reduce((a, [ok, , w]) => a + (ok ? w : 0), 0);
  const pct = Math.round((got / total) * 100);
  const next = checks.find(([ok]) => !ok);

  const jump = (anchor) => document.querySelector(`[data-section-anchor="${anchor}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const titles = data.sectionTitles || {};
  const chips = [{ anchor: 'Personal Information', label: 'Personal' }, ...sectionOrder.map(id => ({ anchor: id, label: titles[id] || NAV_LABELS[id] || id }))];

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 50, background: '#F9FAFB', padding: '2px 0 8px', marginBottom: 6, borderBottom: '1px solid #F0F1F3' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
        <div style={{ flex: 1, height: 6, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? '#10B981' : pct >= 45 ? '#F59E0B' : '#EF1D28', borderRadius: 999, transition: 'width 0.4s, background 0.4s' }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', flexShrink: 0 }}>{pct}% complete</span>
      </div>
      {next && <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 7 }}>Next: add {next[1]} to strengthen your resume</div>}
      <div className="re-nav-chips" style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 2 }}>
        {chips.map(c => (
          <button key={c.anchor} type="button" onClick={() => jump(c.anchor)} style={{
            padding: '3px 10px', borderRadius: 999, border: '1px solid #E5E7EB', background: 'white',
            fontSize: 11, fontWeight: 600, color: '#4B5563', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
          }}>{c.label}</button>
        ))}
      </div>
    </div>
  );
};

// ── Upgrade prompt for premium-gated Section Analysis ─────────────────────────
const AnalysisUpgradeModal = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 18, padding: '28px 26px', width: '100%', maxWidth: 400, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>×</button>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: '#FFF1F1', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Lock size={24} color="#EF1D28" />
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#101828' }}>Section Analysis is a Premium feature</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13.5, color: '#6B7280', lineHeight: 1.6 }}>
          Get instant AI feedback on every section of your resume — strengths, missing details and improvement tips. Switch to Premium to unlock it.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Maybe later
          </button>
          <button onClick={() => navigate('/pricing')} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#F97316)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(239,29,40,0.30)' }}>
            Switch to Premium
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const ResumeEditor = ({ data, onChange, isPro = false, isGuest = false, onRequireSignup }) => {
  const { improveText, generateBullets, generateSkillsFromTitle, generateStrengthsFromTitle, loading: aiLoading, dailyLimitError, clearDailyLimitError } = useClaudeAI();
  const { showAlert } = useDialog();
  const [showAnalysisUpgrade, setShowAnalysisUpgrade] = useState(false);
  const proCtx = { isPro, requestUpgrade: () => { if (isGuest) { onRequireSignup?.(); return; } setShowAnalysisUpgrade(true); } };

  const upd = (field, val) => onChange({ ...data, [field]: val });

  // Undo-on-remove: when a list shrinks by one entry, offer a 6-second undo
  const [undo, setUndo] = useState(null);
  const undoTimer = useRef(null);
  const withUndo = (key, label) => (next) => {
    const prev = data[key] || [];
    if (Array.isArray(next) && Array.isArray(prev) && next.length === prev.length - 1) {
      clearTimeout(undoTimer.current);
      setUndo({ key, label, prev });
      undoTimer.current = setTimeout(() => setUndo(null), 6000);
    }
    onChange({ ...data, [key]: next });
  };

  const handleImprove = async (field, text) => {
    if (!text) return;
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const improved = await improveText(text, field);
      if (data.type === 'cover_letter') {
        onChange({ ...data, content: { ...data.content, [field]: improved } });
      } else {
        onChange({ ...data, personalInfo: { ...data.personalInfo, summary: improved } });
      }
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleGenerateBullets = async (expId, role, company, desc) => {
    if (isGuest) { onRequireSignup?.(); return; }
    if (!role) { showAlert('Please enter a job title first.', { type: 'warning', title: 'Missing Info' }); return; }
    try {
      const bullets = await generateBullets(role, company, desc);
      upd('experience', (data.experience || []).map(e => e.id === expId ? { ...e, bullets } : e));
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleImproveDescription = async (expId, desc) => {
    if (!desc) return;
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const improved = await improveText(desc, 'job_description');
      upd('experience', (data.experience || []).map(e => e.id === expId ? { ...e, description: improved } : e));
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleImproveField = async (sectionKey, id, field, text) => {
    if (!text) return;
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const improved = await improveText(text, field);
      upd(sectionKey, (data[sectionKey] || []).map(item => item.id === id ? { ...item, [field]: improved } : item));
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleGenerateSkills = async (jobTitle) => {
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const skills = await generateSkillsFromTitle(jobTitle);
      upd('skills', skills);
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleGenerateStrengths = async (jobTitle) => {
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const strengths = await generateStrengthsFromTitle(jobTitle);
      upd('strengths', strengths);
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleImproveGoals = async (text) => {
    if (!text) return;
    if (isGuest) { onRequireSignup?.(); return; }
    try {
      const improved = await improveText(text, 'career goals');
      upd('goals', improved);
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [premiumSections, setPremiumSections] = useState([]);
  useEffect(() => {
    api.getSectionConfig().then(d => setPremiumSections(d.premiumSections || [])).catch(() => {});
  }, []);

  if (data.type === 'cover_letter') {
    return (
      <PremiumSectionsContext.Provider value={premiumSections}>
        <EditorProContext.Provider value={proCtx}>
          <CoverLetterEditor data={data} onChange={onChange} onImprove={handleImprove} aiLoading={aiLoading} />
          {showAnalysisUpgrade && <AnalysisUpgradeModal onClose={() => setShowAnalysisUpgrade(false)} />}
        </EditorProContext.Provider>
      </PremiumSectionsContext.Provider>
    );
  }

  const isCV = data.type === 'cv';
  const sectionOrder = data.sectionOrder || (isCV ? [...CV_ORDER] : [...DEFAULT_ORDER]);
  const total = sectionOrder.length;

  const moveSection = (idx, dir) => {
    const newOrder = [...sectionOrder];
    const target = idx + dir;
    if (target < 0 || target >= total) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    onChange({ ...data, sectionOrder: newOrder });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sectionOrder.indexOf(active.id);
      const newIndex = sectionOrder.indexOf(over.id);
      onChange({ ...data, sectionOrder: arrayMove(sectionOrder, oldIndex, newIndex) });
    }
  };

  const renderSection = (sectionId, idx) => {
    const moveUp = idx > 0 ? () => moveSection(idx, -1) : null;
    const moveDown = idx < total - 1 ? () => moveSection(idx, 1) : null;
    const titles = data.sectionTitles || {};
    const icons = data.sectionIcons || {};
    const sectionTitle = titles[sectionId] || '';
    const onTitleChange = v => upd('sectionTitles', { ...titles, [sectionId]: v });
    const sectionIcon = sectionId in icons ? icons[sectionId] : (DEFAULT_SECTION_ICONS[sectionId] || '');
    const onIconChange = v => upd('sectionIcons', { ...icons, [sectionId]: v });
    switch (sectionId) {
      case 'experience':
        return <ExperienceSection key="experience" items={data.experience || []} onChange={withUndo('experience', 'Position')} onGenerateBullets={handleGenerateBullets} onImproveDescription={handleImproveDescription} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'education':
        return <EducationSection key="education" items={data.education || []} onChange={withUndo('education', 'Education entry')} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'skills':
        return <SkillsSection key="skills" items={data.skills || []} onChange={withUndo('skills', 'Skill category')} onGenerateSkills={handleGenerateSkills} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} template={data.template} />;
      case 'projects':
        return <ProjectsSection key="projects" items={data.projects || []} onChange={withUndo('projects', 'Project')} onImproveField={handleImproveField} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'strengths':
        return <KeyStrengthsSection key="strengths" value={data.strengths || ''} onChange={v => upd('strengths', v)} onGenerateStrengths={handleGenerateStrengths} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'goals':
        return <GoalsSection key="goals" value={data.goals || ''} onChange={v => upd('goals', v)} onImprove={handleImproveGoals} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'hobbies':
        return <HobbiesSection key="hobbies" value={data.hobbies || ''} onChange={v => upd('hobbies', v)} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'achievements':
        return <AchievementsSection key="achievements" items={data.achievements || []} onChange={withUndo('achievements', 'Achievement')} onImproveField={handleImproveField} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'certifications':
        return <CertSection key="certifications" items={data.certifications || []} onChange={withUndo('certifications', 'Certification')} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'languages':
        return <LanguagesSection key="languages" items={data.languages || []} onChange={withUndo('languages', 'Language')} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'publications':
        return <PublicationsSection key="publications" items={data.publications || []} onChange={withUndo('publications', 'Publication')} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'awards':
        return <AwardsSection key="awards" items={data.awards || []} onChange={withUndo('awards', 'Award')} onImproveField={handleImproveField} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'volunteering':
        return <VolunteeringSection key="volunteering" items={data.volunteering || []} onChange={withUndo('volunteering', 'Volunteering role')} onImproveField={handleImproveField} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'references':
        return <ReferencesSection key="references" items={data.references || []} onChange={withUndo('references', 'Reference')} showAvailableOnRequest={data.referencesAvailableOnRequest} onToggleAvailableOnRequest={v => upd('referencesAvailableOnRequest', v)} onMoveUp={moveUp} onMoveDown={moveDown} sectionTitle={sectionTitle} onTitleChange={onTitleChange} sectionIcon={sectionIcon} onIconChange={onIconChange} />;
      case 'customBlocks':
        return <CustomBlocksSection key="customBlocks" items={data.customBlocks || []} onChange={withUndo('customBlocks', 'Custom block')} onImproveField={handleImproveField} aiLoading={aiLoading} onMoveUp={moveUp} onMoveDown={moveDown} />;
      default:
        return null;
    }
  };

  return (
    <PremiumSectionsContext.Provider value={premiumSections}>
    <EditorProContext.Provider value={proCtx}>
      <div>
        <style>{`
          @media (max-width: 600px) {
            /* Form grids collapse to single column */
            .re-grid-2 { grid-template-columns: 1fr !important; }
            .re-grid-2 > [style*="span 2"],
            .re-grid-2 > div[style*="gridColumn"] { grid-column: span 1 !important; }
            .re-grid-3 { grid-template-columns: 1fr !important; }

            /* Section header: hide low-priority controls */
            .re-sec-move { display: none !important; }
            .re-sec-drag { display: none !important; }

            /* Section body padding tighter */
            .re-sec-body { padding: 2px 12px 14px !important; }

            /* Icon edit box smaller */
            .re-icon-edit { width: 40px !important; height: 40px !important; }
          }

          /* Reorder arrows: hidden until the header is hovered (reduces clutter) */
          .re-sec-card .re-sec-move { opacity: 0; transition: opacity 0.15s; }
          .re-sec-card:hover .re-sec-move, .re-sec-card:focus-within .re-sec-move { opacity: 1; }

          /* Drag handle: ghost by default, surfaces on hover */
          .re-sec-card:hover .re-sec-drag { background: #F3F4F6 !important; color: #6B7280 !important; }

          /* Card hover affordance */
          .re-sec-card { transition: border-color 0.15s, box-shadow 0.2s; }
          .re-sec-card:hover { box-shadow: 0 3px 10px rgba(16,24,40,0.07); }

          /* Icon edit box + title pencil affordances */
          .re-icon-edit:hover { border-color: #EF1D28 !important; }
          .re-icon-edit:hover span { color: #EF1D28 !important; }
          .re-title-pen { opacity: 0.55; transition: opacity 0.12s; }
          .re-title-wrap:hover .re-title-pen { opacity: 1; stroke: #EF1D28; }

          /* Icon action buttons (analysis eye, entry trash/collapse, help) */
          .re-ic-btn { background: none; border: none; color: #98A2B3; cursor: pointer;
                       width: 30px; height: 30px; border-radius: 8; flex-shrink: 0; padding: 0;
                       display: inline-flex; align-items: center; justify-content: center;
                       transition: background 0.12s, color 0.12s; }
          .re-ic-btn:hover { background: #F3F4F6; color: #374151; }
          .re-ic-danger { color: #F97066; }
          .re-ic-danger:hover { background: #FEF3F2; color: #D92D20; }

          /* Input focus ring in theme color */
          .re-sec-body input:focus, .re-sec-body textarea:focus, .re-sec-body select:focus {
            border-color: #EF1D28 !important;
            box-shadow: 0 0 0 3px rgba(239,29,40,0.08);
          }

          /* Smooth expand */
          .re-sec-body { animation: reSecOpen 0.18s ease; }
          @keyframes reSecOpen { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

          /* Analysis loading spinner */
          .re-spin { animation: reSpin 0.7s linear infinite; }
          @keyframes reSpin { to { transform: rotate(360deg); } }

          /* Consistent inline delete buttons */
          .re-x { background: none; border: none; color: #C2C8D0; cursor: pointer; font-size: 16px; line-height: 1;
                  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
                  display: inline-flex; align-items: center; justify-content: center;
                  transition: background 0.12s, color 0.12s; }
          .re-x:hover { background: #FEF2F2; color: #DC2626; }

          .re-nav-chips { scrollbar-width: none; }
          .re-nav-chips::-webkit-scrollbar { display: none; }
        `}</style>
        <EditorNav data={data} sectionOrder={sectionOrder} />
        <PersonalSection data={data.personalInfo || {}} onChange={v => upd('personalInfo', v)} onImprove={handleImprove} aiLoading={aiLoading} template={data.template} />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
            {sectionOrder.map((sectionId, idx) => {
              const section = renderSection(sectionId, idx);
              if (!section) return null;
              return (
                <SortableSectionItem key={sectionId} id={sectionId}>
                  {section}
                </SortableSectionItem>
              );
            })}
          </SortableContext>
        </DndContext>

        {/* Undo toast for removed entries */}
        {undo && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 2000, background: '#111827', color: 'white', borderRadius: 10, padding: '10px 18px', display: 'flex', gap: 16, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', fontSize: 13 }}>
            <span>{undo.label} removed</span>
            <button onClick={() => { clearTimeout(undoTimer.current); onChange({ ...data, [undo.key]: undo.prev }); setUndo(null); }}
              style={{ background: 'none', border: 'none', color: '#FBBF24', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0, fontFamily: 'Inter, sans-serif' }}>
              Undo
            </button>
            <button onClick={() => setUndo(null)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
          </div>
        )}

        {/* Premium upgrade prompt for Section Analysis */}
        {showAnalysisUpgrade && <AnalysisUpgradeModal onClose={() => setShowAnalysisUpgrade(false)} />}

        {/* Daily AI limit modal */}
        {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
      </div>
    </EditorProContext.Provider>
    </PremiumSectionsContext.Provider>
  );
};

export default ResumeEditor;
